<template>
    <div>
        <el-dialog :title="file.originalName" :visible.sync="dialogVisible" width="70%" top="5vh"
            @close="viewFileClose($event)" @open="dialog_open">
            <!-- Excel预览：使用 xlsx 库纯前端渲染 HTML 表格 -->
            <div v-if="fileType === 'excel'" class="excel-preview-wrapper">
                <div v-if="excelLoading" class="excel-loading">
                    <i class="el-icon-loading"></i> 正在解析 Excel 文件...
                </div>
                <div v-else-if="excelError" class="excel-error">
                    <i class="el-icon-warning"></i> {{ excelError }}
                </div>
                <template v-else>
                    <!-- 多 Sheet 切换标签 -->
                    <div v-if="sheetNames.length > 1" class="excel-sheet-tabs">
                        <span
                            v-for="(name, index) in sheetNames"
                            :key="index"
                            class="excel-sheet-tab"
                            :class="{ active: activeSheetIndex === index }"
                            @click="switchSheet(index)"
                        >{{ name }}</span>
                    </div>
                    <!-- 单 Sheet 标题 -->
                    <div v-else-if="sheetNames.length === 1" class="excel-sheet-name">
                        {{ sheetNames[0] }}
                    </div>
                    <div ref="tableWrapper" class="excel-table-wrapper" v-html="excelHtml"></div>
                </template>
            </div>
            <!-- Word预览 -->
            <vue-office-docx v-if="fileType === 'docx'" :src="fileData" />
        </el-dialog>
    </div>
</template>

<script>
import VueOfficeDocx from '@vue-office/docx'
import * as XLSX from 'xlsx'
import ExcelJS from 'exceljs'
import { viewFileApi } from '@/Api/fileApi'

export default {
    name: 'viewFileMin',
    components: {
        VueOfficeDocx
    },
    props: ['file', 'visible'],
    computed: {
        dialogVisible: {
            get() { return this.visible; },
            set(value) { this.$emit('updateVisible', value); }
        }
    },
    data() {
        return {
            fileData: '',           // docx 用 BlobURL
            fileType: '',
            ext: '',
            excelHtml: '',
            excelLoading: false,
            excelError: '',
            sheetNames: [],         // 所有 sheet 名称
            activeSheetIndex: 0,    // 当前激活的 sheet 下标
            workbook: null,         // 缓存 xlsx workbook
            ejWorkbook: null,       // 缓存 exceljs workbook（用于图片）
            // 每个 sheet 的图片映射：{ sheetIndex: [ { row, col, dataUrl } ] }
            sheetImagesMap: {},
        }
    },
    methods: {
        async preview() {
            this.ext = (this.file.originalName || '').split('.').pop().toLowerCase();

            if (['doc', 'docx'].includes(this.ext)) {
                this.fileType = 'docx';
            } else if (['xls', 'xlsx'].includes(this.ext)) {
                this.fileType = 'excel';
            }

            const blob = await viewFileApi(this.file.id, this);
            const arrayBuffer = await blob.arrayBuffer();

            if (['xls', 'xlsx'].includes(this.ext)) {
                this.excelLoading = true;
                this.excelError = '';
                try {
                    // 1. xlsx 解析（渲染表格用）
                    this.workbook = XLSX.read(arrayBuffer, {
                        type: 'array',
                        cellDates: true,
                        cellNF: true
                    });
                    this.sheetNames = this.workbook.SheetNames;
                    if (!this.sheetNames.length) {
                        throw new Error('Excel 文件中没有工作表');
                    }

                    // 2. ExcelJS 解析（提取图片用）
                    this.sheetImagesMap = {};
                    try {
                        const ejWb = new ExcelJS.Workbook();
                        await ejWb.xlsx.load(arrayBuffer.slice(0)); // slice 避免 ArrayBuffer detach
                        this.ejWorkbook = ejWb;
                        ejWb.worksheets.forEach((ws, wsIdx) => {
                            const imgs = ws.getImages();
                            if (!imgs || !imgs.length) return;
                            const list = [];
                            imgs.forEach(img => {
                                try {
                                    const media = ejWb.model.media.find(m => m.index === img.imageId);
                                    if (!media) return;
                                    const mime = `image/${media.extension === 'jpg' ? 'jpeg' : media.extension}`;
                                    // buffer → base64
                                    let binary = '';
                                    const bytes = new Uint8Array(media.buffer);
                                    for (let i = 0; i < bytes.byteLength; i++) {
                                        binary += String.fromCharCode(bytes[i]);
                                    }
                                    const dataUrl = `data:${mime};base64,${btoa(binary)}`;
                                    // 锚点：取左上角行列（0-based → 转成 1-based 与表格对齐）
                                    const tl = img.range && img.range.tl;
                                    if (tl) {
                                        list.push({
                                            row: (tl.nativeRow != null ? tl.nativeRow : tl.row) + 1,
                                            col: (tl.nativeCol != null ? tl.nativeCol : tl.col) + 1,
                                            dataUrl
                                        });
                                    }
                                } catch (imgErr) {
                                    console.warn('图片提取失败:', imgErr);
                                }
                            });
                            if (list.length) this.sheetImagesMap[wsIdx] = list;
                        });
                    } catch (ejErr) {
                        // ExcelJS 解析失败不影响表格显示
                        console.warn('ExcelJS 解析图片失败:', ejErr);
                    }

                    this.activeSheetIndex = 0;
                    this.renderSheet(0);
                } catch (e) {
                    console.error('Excel 解析失败:', e);
                    this.excelError = 'Excel 文件解析失败：' + (e.message || '文件格式不支持');
                } finally {
                    this.excelLoading = false;
                }
            } else {
                if (this.fileData && typeof this.fileData === 'string') {
                    URL.revokeObjectURL(this.fileData);
                }
                this.fileData = URL.createObjectURL(blob);
            }
        },

        // 渲染指定下标的 sheet（表格 + 图片）
        renderSheet(index) {
            const sheetName = this.sheetNames[index];
            const sheet = this.workbook.Sheets[sheetName];
            this.excelHtml = XLSX.utils.sheet_to_html(sheet, {
                editable: false,
                header: '',
                footer: ''
            });
            // DOM 更新后插入图片
            this.$nextTick(() => {
                this.injectImages(index);
            });
        },

        // 将图片插入到对应单元格
        injectImages(sheetIndex) {
            const images = this.sheetImagesMap[sheetIndex];
            if (!images || !images.length) return;
            const wrapper = this.$refs.tableWrapper;
            if (!wrapper) return;
            const table = wrapper.querySelector('table');
            if (!table) return;
            const rows = table.querySelectorAll('tr');

            images.forEach(({ row, col, dataUrl }) => {
                // row/col 都是 1-based
                const tr = rows[row - 1];
                if (!tr) return;
                const tds = tr.querySelectorAll('td, th');
                const td = tds[col - 1];
                if (!td) return;
                const img = document.createElement('img');
                img.src = dataUrl;
                img.style.cssText = 'max-width:200px;max-height:200px;display:block;margin-top:4px;';
                td.appendChild(img);
            });
        },

        // 切换 sheet
        switchSheet(index) {
            if (index === this.activeSheetIndex) return;
            this.activeSheetIndex = index;
            this.renderSheet(index);
        },

        viewFileClose() {
            if (this.fileData && typeof this.fileData === 'string') {
                URL.revokeObjectURL(this.fileData);
            }
            this.fileData = '';
            this.fileType = '';
            this.ext = '';
            this.excelHtml = '';
            this.excelError = '';
            this.sheetNames = [];
            this.activeSheetIndex = 0;
            this.workbook = null;
            this.ejWorkbook = null;
            this.sheetImagesMap = {};
            this.dialogVisible = false;
        },
        dialog_open() {
            this.preview();
        },
    },
    mounted() {},
}
</script>

<style scoped>
/deep/ .el-dialog__header  {
    padding: 14px 10px;
}
/deep/ .el-dialog__body {
    padding: 8px 8px;
}
.excel-preview-wrapper {
    max-height: 60vh;
    overflow: auto;
}
.excel-loading {
    text-align: center;
    padding: 40px;
    color: #909399;
}
.excel-loading i {
    font-size: 24px;
    margin-right: 8px;
}
.excel-error {
    text-align: center;
    padding: 40px;
    color: #F56C6C;
}
.excel-error i {
    font-size: 24px;
    margin-right: 8px;
}
/* 多 Sheet 切换标签栏 */
.excel-sheet-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 2px;
    padding: 6px 8px 0;
    background: #f5f7fa;
    border-bottom: 2px solid #dcdfe6;
    position: sticky;
    top: 0;
    z-index: 1;
}
.excel-sheet-tab {
    padding: 6px 16px;
    font-size: 13px;
    color: #606266;
    cursor: pointer;
    border: 1px solid #dcdfe6;
    border-bottom: none;
    border-radius: 4px 4px 0 0;
    background: #fff;
    transition: all 0.2s;
    user-select: none;
}
.excel-sheet-tab:hover {
    color: #409EFF;
    background: #ecf5ff;
}
.excel-sheet-tab.active {
    color: #409EFF;
    font-weight: 600;
    background: #fff;
    border-color: #409EFF;
    border-bottom: 2px solid #fff;
    margin-bottom: -2px;
}
/* 单 Sheet 标题 */
.excel-sheet-name {
    padding: 8px 12px;
    font-weight: 600;
    color: #409EFF;
    border-bottom: 1px solid #ebeef5;
    background: #f5f7fa;
}
.excel-table-wrapper {
    overflow: auto;
}
.excel-table-wrapper :deep(table) {
    border-collapse: collapse;
    width: 100%;
    font-size: 13px;
}
.excel-table-wrapper :deep(th),
.excel-table-wrapper :deep(td) {
    border: 1px solid #dcdfe6;
    padding: 6px 10px;
    text-align: left;
    white-space: pre-wrap;
    word-break: break-all;
    max-width: 300px;
}
.excel-table-wrapper :deep(th) {
    background: #f5f7fa;
    font-weight: 600;
    color: #303133;
}
.excel-table-wrapper :deep(tr:nth-child(even) td) {
    background: #fafafa;
}
.excel-table-wrapper :deep(tr:hover td) {
    background: #ecf5ff;
}
</style>
